import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import styled from 'styled-components';
import debounce from 'lodash.debounce';

import { Text, Separator, Button, Row } from '@cuemath/comps';

import { getAll as getTeacherList } from 'actions/teacher-list';
import { TEACHERS_PER_PAGE } from 'constants/list';

import { EVENT_TYPE } from 'constants/logger';
import {
  ButtonWrapper,
  Head,
  Message,
  FieldWrapper,
  FormWrapper,
  getOptions,
  Field,
  SelectInput,
} from 'modules/form';

import { OPTIONS } from 'constants/form';
import {
  useStudentCoursePatch,
  invalidateStudentCoursesGetAll,
} from '../../actions/studentsv2';
import { store } from 'redux-rest-reducer';
import { trackEvent } from 'modules/analytics';

const BasisContentContainer = styled(Row)`
  flex-basis: 75%;
`;

const AssignTeacherToSATDemo = ({ studentId }) => {
  const [teacherId, setTeacherId] = useState('');
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [search, setSearch] = useState(null);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const dispatch = useDispatch();
  const listParams = { page: 1, per_page: TEACHERS_PER_PAGE, search };
  const teachersData = useSelector(({ teacherList }) => {
    return store.getAll(teacherList, listParams);
  });

  const onAssignTeacherPostComplete = errorMessage => {
    if (errorMessage) {
      trackEvent(EVENT_TYPE.SAT_DEMO_ASSIGNED_FAILED, {
        student_id: studentId,
        message: errorMessage,
      });
      setError(errorMessage);
      setMessage(null);
    } else {
      trackEvent(EVENT_TYPE.SAT_DEMO_ASSIGNED, {
        student_id: studentId,
      });
      setDisableSubmit(true);
      setMessage('Teacher assigned successfully');
      setError(null);
      invalidateStudentCoursesGetAll(studentId);
    }
  };

  const { patch: assignTeacherPost, isProcessing } = useStudentCoursePatch({
    onComplete: onAssignTeacherPostComplete,
  });

  const onTextChange = (val, reason) => {
    const { action } = reason;

    const listParamsToGet = { page: 1, per_page: TEACHERS_PER_PAGE, search: val };
    const filterData = {
      page: 1,
      per_page: TEACHERS_PER_PAGE,
      state: ['ACT', 'FDTR'],
      search: val,
    };

    if (val.length <= 4 && action !== 'menu-close') {
      setError('Please enter more than 4 characters');
      return;
    }

    if (error) {
      setError(null);
    }

    if (action !== 'menu-close' || val.length !== 0) {
      setSearch(val);
      dispatch(getTeacherList(listParamsToGet, filterData));
    }
  };

  const onSubmit = event => {
    event.preventDefault();
    assignTeacherPost(studentId, {
      action: 'ASSIGN_SAT_DEMO',
      action_data: {
        primary_teacher_id: teacherId,
      },
    });
  };

  const renderFormFields = () => {
    let teacherOptions = [];
    const { data } = teachersData || {};
    if (Array.isArray(data) && data.length > 0) {
      teacherOptions = getOptions({
        field: OPTIONS.TEACHERS_LIST,
        teachersDataOptions: data.map(teacher => {
          return teacher;
        }),
      });
    }

    const handleSelectTeacher = teacher => {
      const { value: teacherId } = teacher || {};
      setTeacherId(teacherId);
    };

    const handleInputChange = debounce((val, reason) => {
      onTextChange(val, reason);
    }, 1000);

    return (
      <Field key="region">
        <Text size="normal">Choose a teacher</Text>
        <BasisContentContainer>
          <SelectInput
            name="teacher"
            options={teacherOptions}
            onInputChange={handleInputChange}
            onChange={handleSelectTeacher}
            isOptionDisabled={option => option.disabled}
            isMulti={false}
            maxMenuHeight={112}
          />
        </BasisContentContainer>
      </Field>
    );
  };

  return (
    <form onSubmit={onSubmit}>
      <FormWrapper>
        <Head>
          <Text size="xlarge" align="center">
            Assign a teacher
          </Text>
        </Head>
        <Separator transparent height="50" />
        <FieldWrapper>
          {renderFormFields()}
          <Separator transparent height="30" />
          {error && <Message error={true}>{error}</Message>}
          {message && <Message>{message}</Message>}
          {(error || message) && <Separator transparent height="30" />}
          <ButtonWrapper>
            <Button
              disabled={disableSubmit || isProcessing}
              label="Confirm"
              id="submit"
              busy={isProcessing}
            />
          </ButtonWrapper>
          <Separator transparent height="40" />
        </FieldWrapper>
      </FormWrapper>
    </form>
  );
};

export default AssignTeacherToSATDemo;
